import {
  useState,
  useMemo,
} from 'react';
import {
  ShoppingCart,
  Clock,
  Truck,
  Package,
  AlertTriangle,
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CrudToolbar } from '@/components/ui/crud-toolbar';
import {
  FormModal,
  FormField,
  FormInput,
  FormSelect,
  FormTextarea,
} from '@/components/ui/form-modal';
import {
  usePurchaseOrders,
  PurchaseOrderCard,
  PurchaseOrderDetailModal,
  type POStatus,
  type PurchaseOrder,
} from '@/features/procurement';
import { toast } from 'sonner';

interface StatusTab {
  value: POStatus | undefined;
  label: string;
}

const STATUS_TABS: StatusTab[] = [
  {
    value: undefined,
    label: 'All',
  },
  {
    value: 'pending',
    label: 'Pending',
  },
  {
    value: 'approved',
    label: 'Approved',
  },
  {
    value: 'shipped',
    label: 'Shipped',
  },
  {
    value: 'delivered',
    label: 'Delivered',
  },
];

interface POFormData {
  supplierId: string;
  deliveryDate: string;
  notes: string;
}

export function OrdersView() {
  const [selectedStatus, setSelectedStatus] = useState<POStatus | undefined>(undefined);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedPO, setSelectedPO] = useState<PurchaseOrder | null>(null);
  const [formData, setFormData] = useState<POFormData>({
    supplierId: '',
    deliveryDate: '',
    notes: '',
  });

  const queryResult = usePurchaseOrders({ status: selectedStatus });
  const data = queryResult.data;
  const isLoading = queryResult.isLoading;
  const error = queryResult.error;
  const refetch = queryResult.refetch;
  const { data: allData } = usePurchaseOrders();

  const purchaseOrders = data?.data ?? [];

  const stats = useMemo(() => {
    const pos = allData?.data ?? [];
    return {
      total: pos.length,
      pending: pos.filter((p) => p.status === 'pending').length,
      inTransit: pos.filter((p) => ['shipped', 'acknowledged'].includes(p.status)).length,
      totalValue: pos.reduce((sum, p) => sum + p.total, 0),
    };
  }, [allData]);

  const handleAddPO = () => {
    setFormData({
      supplierId: '',
      deliveryDate: '',
      notes: '',
    });
    setShowAddModal(true);
  };

  const handleSavePO = () => {
    if (!formData.supplierId || !formData.deliveryDate) {
      toast.error('Please fill in all required fields');
      return;
    }
    toast.success('Purchase order created');
    setShowAddModal(false);
    refetch();
  };

  const handlePOClick = (po: PurchaseOrder) => {
    setSelectedPO(po);
  };

  const handleCloseDetail = () => {
    setSelectedPO(null);
  };

  const handleApprovePO = (poId: string) => {
    toast.success(`Purchase order ${poId} approved`);
    setSelectedPO(null);
    refetch();
  };

  const handleRejectPO = (poId: string) => {
    toast.success(`Purchase order ${poId} cancelled`);
    setSelectedPO(null);
    refetch();
  };

  const handleSendToSupplier = (poId: string) => {
    toast.success(`Purchase order ${poId} sent to supplier`);
    setSelectedPO(null);
    refetch();
  };

  const handleBulkUpload = (file: File) => {
    toast.success(`Processing ${file.name}...`);
    setTimeout(() => {
      toast.success('Imported 5 purchase orders');
      refetch();
    }, 1500);
  };

  const exportData = purchaseOrders.map((po) => ({
    poNumber: po.poNumber,
    supplier: po.supplierName,
    status: po.status,
    total: po.total,
    currency: po.currency,
    deliveryDate: po.expectedDeliveryDate,
    createdAt: po.createdAt,
  }));

  return (
    <>
      <CrudToolbar
        entityName="Purchase Order"
        entityNamePlural="Purchase Orders"
        selectedCount={0}
        onAdd={handleAddPO}
        onBulkUpload={handleBulkUpload}
        exportData={exportData}
        exportFilename="purchase-orders.csv"
        canEdit={false}
        canDelete={false}
      />

      <FormModal
        title="Create Purchase Order"
        description="Create a new purchase order"
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSave={handleSavePO}
        saveLabel="Create PO"
      >
        <div className="grid gap-4 md:grid-cols-2">
          <FormField label="Supplier" required>
            <FormSelect
              value={formData.supplierId}
              onChange={(e) => setFormData({
                ...formData,
                supplierId: e.target.value,
              })}
              placeholder="Select supplier..."
              options={[
                {
                  value: 'sup-001',
                  label: 'Ocean Fresh Seafood',
                },
                {
                  value: 'sup-002',
                  label: 'Premium Meats Inc',
                },
                {
                  value: 'sup-003',
                  label: 'Farm Direct Produce',
                },
              ]}
            />
          </FormField>
          <FormField label="Expected Delivery" required>
            <FormInput
              type="date"
              value={formData.deliveryDate}
              onChange={(e) => setFormData({
                ...formData,
                deliveryDate: e.target.value,
              })}
            />
          </FormField>
        </div>
        <FormField label="Notes">
          <FormTextarea
            value={formData.notes}
            onChange={(e) => setFormData({
              ...formData,
              notes: e.target.value,
            })}
            placeholder="Special instructions..."
            rows={2}
          />
        </FormField>
      </FormModal>

      {selectedPO && (
        <PurchaseOrderDetailModal
          po={selectedPO}
          isOpen={true}
          onClose={handleCloseDetail}
          onApprove={handleApprovePO}
          onReject={handleRejectPO}
          onSendToSupplier={handleSendToSupplier}
        />
      )}

      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total POs</CardTitle>
            <ShoppingCart className="h-5 w-5 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Pending Approval
            </CardTitle>
            <Clock className="h-5 w-5 text-amber-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-amber-600">{stats.pending}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">In Transit</CardTitle>
            <Truck className="h-5 w-5 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{stats.inTransit}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Value</CardTitle>
            <Package className="h-5 w-5 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              ${stats.totalValue.toLocaleString()}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="flex flex-wrap gap-2">
        {STATUS_TABS.map((tab) => (
          <Button
            key={tab.label}
            variant={selectedStatus === tab.value ? 'default' : 'outline'}
            size="sm"
            onClick={() => setSelectedStatus(tab.value)}
          >
            {tab.label}
          </Button>
        ))}
      </div>

      {isLoading && (
        <div className="grid gap-4 md:grid-cols-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={`skel-${i.toString()}`} className="h-48 animate-pulse rounded-lg bg-muted" />
          ))}
        </div>
      )}

      {!isLoading && error && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <AlertTriangle className="mb-4 h-12 w-12 text-red-500" />
            <p className="text-lg font-medium">Failed to load purchase orders</p>
            <Button variant="outline" className="mt-4" onClick={() => refetch()}>
              Try Again
            </Button>
          </CardContent>
        </Card>
      )}

      {!isLoading && !error && purchaseOrders.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <ShoppingCart className="mb-4 h-12 w-12 text-muted-foreground" />
            <p className="text-lg font-medium">No purchase orders found</p>
          </CardContent>
        </Card>
      )}

      {!isLoading && !error && purchaseOrders.length > 0 && (
        <div className="grid gap-4 md:grid-cols-2">
          {purchaseOrders.map((po) => (
            <PurchaseOrderCard key={po.poId} po={po} onClick={() => handlePOClick(po)} />
          ))}
        </div>
      )}
    </>
  );
}
